<template>
  <Navigation :show="false" />
  <Layout>
    <template v-slot:main>
      <div class="card" v-if="question">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center fw-bold">
            <span>{{ question.testName }}</span>
            <span>
              [{{ question.totalOpened }}/{{ question.totalQuestions }}]
              <vue-countdown :time="getCountdownTime()" :interval="1000" @end="() => onCountdownEndReached()"
                v-slot="{ days, hours, minutes, seconds, milliseconds }" class="fw-bold">
                <span v-if="hours > 0">{{ addLeadingZero(hours) }}:</span>
                {{ addLeadingZero(minutes) }}:{{ addLeadingZero(seconds) }}
              </vue-countdown>
            </span>
          </div>
        </div>
        <div class="card-body">
          <div v-html="DOMPurify.sanitize(question.question)"
            class="card-text border border-3 rounded-2 opacity-100 p-3 pb-0 mb-3"></div>
          <table class="table table-hover">
            <tbody ref="tbody">
              <tr v-for="(answ, index) in question.answers" :key="index"
                :class="(selected.includes(index)) ? 'test-row table-active' : 'test-row'" @click="selectAnswer(index)">
                <th scope="row" class="test-properties">
                  <input class="form-check-input" type="checkbox" :checked="selected.includes(index)"
                    v-if="question.multiChoice" :disabled="disabled">
                  <input class="form-check-input" type="radio" :checked="selected.includes(index)" :disabled="disabled"
                    v-else>
                </th>
                <td v-html="DOMPurify.sanitize(answ)"></td>
              </tr>
            </tbody>
          </table>
          <div class="btn-group float-end">
            <button type="button" class="btn btn-sm btn-primary" @click="finishTestButtonClickHandler"
              :disabled="disabled" v-if="question.totalOpened == question.totalQuestions">
              {{ t('page.test.end') }}
            </button>
            <button type="button" class="btn btn-sm btn-primary" @click="nextQuestion" :disabled="disabled" v-else>
              {{ t('page.test.next') }}
            </button>
          </div>
        </div>
      </div>
      <div class="card mb-3" v-else>
        <div class="card-body">
          <p class="card-text">{{ t('page.test.loading') }}</p>
        </div>
      </div>
    </template>
    <template v-slot:image>
      <CameraFeed :workId="question.workId" v-if="question" />
    </template>
    <template v-slot:button>
      <button type="button" class="btn btn-sm btn-primary" @click="finishTestButtonClickHandler">
        {{ t('page.test.logout.end') }}
      </button>
    </template>
  </Layout>
</template>

<script setup lang="ts">
import Layout from "@/components/Layout.vue";
import CameraFeed from "@/components/CameraFeed.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { MainService } from "@/services/main.service";
import { useRouter } from "vue-router";
import * as DOMPurify from "dompurify";
import { QuestionModel } from "@/models/question.model";
import { AlertManager } from "@/services/alert.manager";
import VueCountdown from '@chenfengyuan/vue-countdown';
import Navigation from "@/components/Navigation.vue";
import { useLogout } from "@/hooks/logout.hook";
import { useTranslate } from "@/hooks/translate.hook";
import Swal from "sweetalert2";

const question = ref<QuestionModel>()
const selected = ref<number[]>([])
const disabled = ref<boolean>(true)
const workId = ref<number>()
const countdownEndReached = ref<boolean>(false);
const showCheatingAlert = ref<boolean>(true);
const logout = useLogout()
const router = useRouter()
const t = useTranslate()

function addLeadingZero(num: number) {
  let numStr = num.toString();
  if (numStr.length === 1) {
    numStr = '0' + numStr;
  }
  return numStr;
}

const isFocused = computed(() => {
  if (isDebugEnabled()) {
    return true
  }
  return isWindowFocused.value && isMouseFocused.value;
});

const isWindowFocused = ref(true);
const isMouseFocused = ref(true);

watch([isFocused], async () => {
  if (showCheatingAlert.value) {
    showCheatingAlert.value = false
    MainService.saveStudentCheatingAttempt()
      .then(rsp => console.log('Cheating recorded'))
      .catch(e => (e.message == 'CREATE_FAILED') ? showResult() : logout())
    await AlertManager.warning(
      t('page.test.focus.title'),
      t('page.test.focus.desc'),
    )
    showCheatingAlert.value = true
  }
});

const handleVisibilityChange = () => {
  if (isDebugEnabled()) {
    isWindowFocused.value = true
    return
  }

  isWindowFocused.value = !document.hidden;
};

function isMouseInsideWindow(event: MouseEvent): boolean {

  const delta = 5;

  return (
    event.clientX >= delta &&
    event.clientY >= delta &&
    event.clientX <= window.innerWidth - delta &&
    event.clientY <= window.innerHeight - delta
  );
}

function handleMouseMoveEvent(event: MouseEvent) {
  if (isDebugEnabled()) {
    isMouseFocused.value = true
    return
  }

  if (isMouseInsideWindow(event)) {
    isMouseFocused.value = true
  } else {
    isMouseFocused.value = false
  }
}

let interval = setInterval(() => {
  isWindowFocused.value = document.hasFocus()
}, 500)

onMounted(() => {
  handleVisibilityChange();
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('mousemove', handleMouseMoveEvent);
  document.addEventListener('mouseleave', handleMouseMoveEvent)
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('mousemove', handleMouseMoveEvent);
  document.removeEventListener('mouseleave', handleMouseMoveEvent)
  clearInterval(interval)
});

function isDebugEnabled() {
  if (process.env.VUE_APP_ALLOW_DEBUG_MODE === "true")
    return localStorage.getItem('debug') != null && localStorage.getItem('debug') == '1'
  return false
}

async function onCountdownEndReached() {
  if (!countdownEndReached.value) {
    countdownEndReached.value = true
    await MainService.finishCurrentStudentTest(question.value!.workId)
    showResult()
  }
}

function selectAnswer(index: number) {
  if (disabled.value) return;
  if (question.value?.multiChoice) {
    // Multiple answers
    if (selected.value.includes(index)) {
      selected.value = selected.value.filter(a => a !== index)
      return
    }

    // Samo dodaj odgovor
    selected.value.push(index)
    return
  }

  // Single choice, remove if it exists
  if (selected.value.length === 1) {
    if (selected.value[0] === index) {
      selected.value = []
      return;
    }

    selected.value = []
    selected.value.push(index)
    return;
  }

  // dodaj odgovor
  selected.value.push(index)
}

function nextQuestion() {
  if (question.value == undefined) {
    return
  }

  disabled.value = true
  const answers = selected.value.map(idx => question.value!.answers[idx])
  question.value = undefined
  MainService.sendAnswers(answers)
    .then(rsp => {
      selected.value = []
      loadNewQuestion()
    })
    .catch(e => {
      if (e.message == 'NOT_FOUND' || e.message == 'CREATE_FAILED' || e.message == "UPDATE_FAILED") {
        showResult()
        return
      }
      logout()
    })
}

function loadNewQuestion() {
  MainService.getNextStudentTestQuestion()
    .then(rsp => {
      question.value = rsp.data
      workId.value = rsp.data.workId
      sessionStorage.setItem('std-work-id', rsp.data.workId)
      disabled.value = false
    })
    .catch(e => {
      // Make sure the work id is undefined if its not parsable
      const parsed = parseInt(sessionStorage.getItem('std-work-id') ?? "")
      workId.value = isNaN(parsed) ? undefined : parsed;

      if (e.message == 'NOT_FOUND' || e.message == 'CREATE_FAILED' || e.message == "UPDATE_FAILED") {
        showResult()
        return
      }
      logout()
    })
}

function finishTestButtonClickHandler() {
  if (question.value == undefined) return
  if (question.value.totalOpened != question.value.totalQuestions) {
    AlertManager.confirm(
      t('page.test.confirm.title'),
      t('page.test.confirm.desc'),
      t('page.test.confirm.yes'),
      t('page.test.confirm.no'),
      () => {
        finishCurrentTest()
      })
    return
  }
  finishCurrentTest()
}

async function finishCurrentTest() {
  if (question.value == undefined) return
  AlertManager.showLoading()

  try {
    disabled.value = true
    const answers = selected.value.map(idx => question.value!.answers[idx])
    await MainService.sendAnswers(answers)
    await MainService.finishCurrentStudentTest(question.value!.workId)
    Swal.close()
    showResult()
  } catch (e: any) {
    console.error(e)
    Swal.close()
    if (e.message == 'NOT_FOUND' || e.message == 'CREATE_FAILED' || e.message == "UPDATE_FAILED") {
      showResult()
      return
    }
    logout()
  }
}

function showResult() {
  if (workId.value == undefined) {
    router.push({
      path: '/list'
    })
    return
  }

  router.push({
    path: '/result/' + workId.value
  })
}

function getCountdownTime() {
  if (question.value == undefined)
    return 0
  return new Date(question.value?.deadline).getTime() - new Date().getTime()
}

onMounted(() => {
  // Ucitaj inicijalno pitanje
  loadNewQuestion()
})
</script>

<style scoped>
.card-text :deep(img) {
  display: block;
  margin-left: auto;
  margin-right: auto;
  max-width: 80%;
}
</style>